/**
 * AriaKnowledge — Tab "Knowledge".
 * Company description, services, products, FAQ, opening_hours.
 */
import React, { useEffect, useState } from "react";
import { Save, Plus, X, BookOpen, Loader, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAriaCustomer } from "./AriaCustomerArea";

export default function AriaKnowledge() {
    const { activeToken, config, refreshConfig } = useAriaCustomer();
    const [kb, setKb] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!config) return;
        const k = config.knowledge_base || {};
        setKb({
            company_description: k.company_description || "",
            opening_hours: k.opening_hours || "",
            services: (k.services && k.services.length) ? k.services : [],
            products: (k.products && k.products.length) ? k.products : [],
            faq: (k.faq && k.faq.length) ? k.faq : [],
            keywords: (k.keywords && k.keywords.length) ? k.keywords : [],
        });
    }, [config]);

    if (!kb) return <div className="text-white/45 text-[12.5px]">Caricamento…</div>;

    const updField = (k, v) => setKb((s) => ({ ...s, [k]: v }));
    const updItem = (arr, idx, k, v) => setKb((s) => {
        const next = [...(s[arr] || [])];
        next[idx] = { ...next[idx], [k]: v };
        return { ...s, [arr]: next };
    });
    const addItem = (arr, tpl) => setKb((s) => ({ ...s, [arr]: [...(s[arr] || []), tpl] }));
    const rmItem = (arr, idx) => setKb((s) => ({ ...s, [arr]: s[arr].filter((_, i) => i !== idx) }));

    const save = async () => {
        if (!activeToken) return;
        setSaving(true); setError(""); setSaved(false);
        try {
            await api.patch(`/widget/${activeToken}`, { knowledge_base: kb });
            await refreshConfig();
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) { setError(formatApiError(e)); }
        setSaving(false);
    };

    return (
        <div className="max-w-3xl" data-testid="tab-knowledge">
            <h1 className="text-white text-[24px] mb-2" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>Cosa Aria deve sapere</h1>
            <p className="text-white/65 text-[14px] mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
                Più informazioni dai ad Aria, più risposte precise dà ai tuoi clienti. Aggiorna quando vuoi — Aria impara in tempo reale.
            </p>

            <Section title="Chi siamo" icon={BookOpen}>
                <textarea
                    value={kb.company_description}
                    onChange={(e) => updField("company_description", e.target.value)}
                    rows={4}
                    placeholder="Pizzeria di tradizione napoletana dal 1987, lievito madre 48h, ingredienti DOP campani…"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none resize-none"
                    data-testid="input-company-desc"
                />
            </Section>

            <Section title="Orari di apertura" icon={Clock}>
                <input
                    type="text"
                    value={kb.opening_hours}
                    onChange={(e) => updField("opening_hours", e.target.value)}
                    placeholder="lun-ven 9-19 · sab 9-13 · domenica chiuso"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                    data-testid="input-hours"
                />
            </Section>

            <ArrayEditor
                title="Servizi"
                testid="services"
                items={kb.services}
                fields={[
                    { key: "name", label: "Nome servizio", placeholder: "Consulenza brand identity" },
                    { key: "description", label: "Descrizione breve", placeholder: "Analisi + logo + linee guida" },
                    { key: "price", label: "Prezzo (opzionale)", placeholder: "da €890" },
                ]}
                onUpdate={(i, k, v) => updItem("services", i, k, v)}
                onAdd={() => addItem("services", { name: "", description: "", price: "" })}
                onRemove={(i) => rmItem("services", i)}
                addLabel="Aggiungi servizio"
            />

            <ArrayEditor
                title="Prodotti"
                testid="products"
                items={kb.products}
                fields={[
                    { key: "name", label: "Nome prodotto", placeholder: "Margherita DOP" },
                    { key: "description", label: "Descrizione breve", placeholder: "Pomodoro San Marzano, mozzarella di bufala" },
                    { key: "price", label: "Prezzo (opzionale)", placeholder: "€8" },
                ]}
                onUpdate={(i, k, v) => updItem("products", i, k, v)}
                onAdd={() => addItem("products", { name: "", description: "", price: "" })}
                onRemove={(i) => rmItem("products", i)}
                addLabel="Aggiungi prodotto"
            />

            <ArrayEditor
                title="Domande frequenti (FAQ)"
                testid="faq"
                items={kb.faq}
                fields={[
                    { key: "q", label: "Domanda", placeholder: "Avete senza glutine?" },
                    { key: "a", label: "Risposta", placeholder: "Sì, abbiamo 4 opzioni gluten-free preparate in zona separata.", multiline: true },
                ]}
                onUpdate={(i, k, v) => updItem("faq", i, k, v)}
                onAdd={() => addItem("faq", { q: "", a: "" })}
                onRemove={(i) => rmItem("faq", i)}
                addLabel="Aggiungi FAQ"
            />

            {error && <div className="mt-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

            <div className="mt-8 flex items-center gap-3 sticky bottom-4">
                <button type="button" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[13.5px] transition hover:scale-[1.02] disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500, boxShadow: "0 18px 40px -14px rgba(168,85,247,0.5)" }}
                    data-testid="save-knowledge">
                    {saving ? <Loader size={13} className="animate-spin" /> : <Save size={13} />}
                    {saving ? "Salvataggio…" : "Salva knowledge"}
                </button>
                {saved && <span className="text-[12.5px] text-[#86EFAC]">salvato</span>}
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section className="mb-8">
            <div className="flex items-center gap-2 mb-3">
                {Icon && <Icon size={12} className="text-white/45" />}
                <span className="text-[10.5px] uppercase tracking-[0.28em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>{title}</span>
            </div>
            {children}
        </section>
    );
}

function ArrayEditor({ title, testid, items, fields, onUpdate, onAdd, onRemove, addLabel }) {
    return (
        <section className="mb-8" data-testid={`section-${testid}`}>
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10.5px] uppercase tracking-[0.28em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>{title}</span>
                <span className="text-[10.5px] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace" }}>{items.length} {items.length === 1 ? "elemento" : "elementi"}</span>
            </div>
            <div className="space-y-3">
                {items.map((it, i) => (
                    <div key={i} className="rounded-xl p-4 relative" style={{ background: "rgba(15,18,28,0.5)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`${testid}-row-${i}`}>
                        <button type="button" onClick={() => onRemove(i)}
                            className="absolute top-3 right-3 w-6 h-6 grid place-items-center rounded-md hover:bg-white/[0.08] text-white/45 hover:text-white"
                            data-testid={`${testid}-remove-${i}`}>
                            <X size={12} />
                        </button>
                        <div className="space-y-2.5">
                            {fields.map((f) => (
                                <div key={f.key}>
                                    <label className="text-[10.5px] uppercase tracking-[0.2em] text-white/45 block mb-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>{f.label}</label>
                                    {f.multiline ? (
                                        <textarea rows={2} value={it[f.key] || ""}
                                            onChange={(e) => onUpdate(i, f.key, e.target.value)}
                                            placeholder={f.placeholder}
                                            className="w-full px-3 py-2 rounded-md bg-white/[0.03] text-white text-[13px] border border-white/[0.06] focus:border-white/20 outline-none resize-none"
                                            data-testid={`${testid}-${i}-${f.key}`} />
                                    ) : (
                                        <input type="text" value={it[f.key] || ""}
                                            onChange={(e) => onUpdate(i, f.key, e.target.value)}
                                            placeholder={f.placeholder}
                                            className="w-full px-3 py-2 rounded-md bg-white/[0.03] text-white text-[13px] border border-white/[0.06] focus:border-white/20 outline-none"
                                            data-testid={`${testid}-${i}-${f.key}`} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <button type="button" onClick={onAdd}
                className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] text-white/75 hover:text-white hover:bg-white/[0.06] transition border border-white/[0.08]"
                data-testid={`${testid}-add`}>
                <Plus size={12} /> {addLabel}
            </button>
        </section>
    );
}
